<script setup>
// Reklam listesi: kreatif duzeyindeki kayitlar. Kampanyadan farkli olarak
// burada butce/hedef alani yoktur, bunun yerine etkin durum ve bagli oldugu
// reklam seti gosterilir - bir reklamin neden yayinda olmadigi cogunlukla
// effective_status alanindan anlasilir.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { aracCalistir, diziyeCevir, paraBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const route = useRoute();

const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');
const islemdeki = ref('');
const detay = ref(null);

// Tutarlar hesabin kendi biriminde gelir; TRY varsaymak yanlis rakam gosterir.
const birim = computed(() => props.hesapAyrinti?.currency || 'TRY');

const tarihBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime()) ? String(deger) : tarih.toLocaleDateString('tr-TR');
};

// Ayrintida saat de gerekiyor: bir reklamin gun icinde ne zaman durduruldugu
// yalnizca gun gosterilince anlasilmiyor.
const tarihSaatBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  if (Number.isNaN(tarih.getTime())) return String(deger);
  return tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const aktifMi = satir => String(satir?.status || '').toUpperCase() === 'ACTIVE';

// Bir reklamin neden yayinda olmadigi effective_status alanindan anlasilir;
// bu alan ham ingilizce kod dondurdugu icin karsiliklarini esliyoruz.
// Eslesmeyen kod oldugu gibi kaliyor - uydurma karsilik yanlis yonlendirir.
const DURUM_ETIKETLERI = {
  ACTIVE: 'META_ADS.ORTAK.DURUM_AKTIF',
  PAUSED: 'META_ADS.ORTAK.DURUM_DURAKLATILDI',
  DELETED: 'META_ADS.ORTAK.DURUM_SILINDI',
  ARCHIVED: 'META_ADS.ORTAK.DURUM_ARSIVLENDI',
  IN_PROCESS: 'META_ADS.ORTAK.DURUM_ISLENIYOR',
  WITH_ISSUES: 'META_ADS.ORTAK.DURUM_SORUNLU',
  PENDING_REVIEW: 'META_ADS.ORTAK.DURUM_INCELEMEDE',
  DISAPPROVED: 'META_ADS.ORTAK.DURUM_REDDEDILDI',
  PREAPPROVED: 'META_ADS.ORTAK.DURUM_ON_ONAYLI',
  PENDING_BILLING_INFO: 'META_ADS.ORTAK.DURUM_ODEME_BEKLIYOR',
  CAMPAIGN_PAUSED: 'META_ADS.ORTAK.DURUM_KAMPANYA_DURDU',
  ADSET_PAUSED: 'META_ADS.ORTAK.DURUM_SET_DURDU',
};

const durumMetni = deger => {
  const ham = String(deger || '').toUpperCase();
  if (!ham) return '—';
  const ceviri = DURUM_ETIKETLERI[ham];
  return ceviri ? t(ceviri) : ham;
};

const sutunlar = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.ORTAK.AD') },
  { anahtar: 'status', baslik: t('META_ADS.ORTAK.DURUM'), bicim: durumMetni },
  {
    anahtar: 'effective_status',
    baslik: t('META_ADS.REKLAMLAR.ETKIN_DURUM'),
    // Sutunda ham "PENDING_REVIEW" gibi kodlar cikiyordu; ayni esleme.
    bicim: durumMetni,
  },
  {
    anahtar: 'adset_id',
    baslik: t('META_ADS.REKLAMLAR.REKLAM_SETI'),
    // Bazi surumler adset_name dondurur, bazilari yalnizca kimlik verir.
    bicim: (deger, satir) => satir?.adset_name || deger || '—',
  },
  { anahtar: 'created_time', baslik: t('META_ADS.ORTAK.OLUSTURMA'), bicim: tarihBicimle },
]);

// Reklam ayrintisi kreatif ve takip ayarlarini ic ice nesne olarak tasir.
// Bunlari JSON.stringify ile basmak kullaniciya hicbir sey anlatmiyordu; onun
// yerine ad -> Turkce etiket, tarih/para -> bicimli deger, ic ice nesne ->
// "Ust > Alt" satirlari seklinde aciyoruz.
const ALAN_ETIKETLERI = {
  id: 'META_ADS.ALAN.KIMLIK',
  account_id: 'META_ADS.ALAN.HESAP',
  campaign_id: 'META_ADS.ALAN.KAMPANYA',
  campaign_name: 'META_ADS.ALAN.KAMPANYA',
  adset_id: 'META_ADS.REKLAMLAR.REKLAM_SETI',
  adset_name: 'META_ADS.REKLAMLAR.REKLAM_SETI',
  ad_id: 'META_ADS.ALAN.REKLAM',
  name: 'META_ADS.ORTAK.AD',
  status: 'META_ADS.ORTAK.DURUM',
  effective_status: 'META_ADS.REKLAMLAR.ETKIN_DURUM',
  configured_status: 'META_ADS.ALAN.AYARLANAN_DURUM',
  creative: 'META_ADS.ALAN.KREATIF',
  bid_amount: 'META_ADS.ALAN.TEKLIF_TUTARI',
  tracking_specs: 'META_ADS.ALAN.TAKIP',
  conversion_specs: 'META_ADS.ALAN.DONUSUM_AYARI',
  preview_shareable_link: 'META_ADS.ALAN.ONIZLEME_BAGLANTISI',
  issues_info: 'META_ADS.ALAN.SORUNLAR',
  created_time: 'META_ADS.ORTAK.OLUSTURMA',
  updated_time: 'META_ADS.ALAN.GUNCELLEME',
};

// Bicimi alan adindan seciyoruz: Meta ayni adi her kaynakta ayni anlamda
// kullanir. Para alanlari kurus gelir.
const TARIH_ALANLARI = ['created_time', 'updated_time'];
const PARA_ALANLARI = ['bid_amount'];
const DURUM_ALANLARI = ['status', 'effective_status', 'configured_status'];

// Iki seviyeden derini dar panelde okunmaz hale geliyor; daha derini ve uzun
// dizileri sayiyla ozetliyoruz.
const EN_FAZLA_DERINLIK = 2;
const EN_FAZLA_ACILAN_OGE = 3;

// Bilinmeyen alan adi oldugu gibi kalir; uydurma etiket yanlis bilgi verir.
const etiketle = anahtar => {
  const ceviri = ALAN_ETIKETLERI[anahtar];
  return ceviri ? t(ceviri) : anahtar;
};

const duzDeger = (anahtar, deger) => {
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.ORTAK.EVET') : t('META_ADS.ORTAK.HAYIR');
  }
  if (TARIH_ALANLARI.includes(anahtar)) return tarihSaatBicimle(deger);
  if (PARA_ALANLARI.includes(anahtar)) return paraBicimle(deger, birim.value);
  if (DURUM_ALANLARI.includes(anahtar)) return durumMetni(deger);
  return String(deger);
};

const alanlaraCevir = (nesne, atlanacak = []) => {
  const cikti = [];

  const gez = (kaynak, ustAnahtar, ustEtiket, derinlik) => {
    Object.entries(kaynak || {}).forEach(([anahtar, deger]) => {
      if (atlanacak.includes(anahtar)) return;
      // Bos alan hic gosterilmiyor: "—" satirlari dolu alanlari gozden kacirir.
      if (deger === null || deger === undefined || deger === '') return;

      const tamAnahtar = ustAnahtar ? `${ustAnahtar}.${anahtar}` : anahtar;
      const etiket = ustEtiket ? `${ustEtiket} › ${etiketle(anahtar)}` : etiketle(anahtar);

      if (Array.isArray(deger)) {
        // Bos ogeleri once atiyoruz: aksi halde listede "null" yazisi cikiyor.
        const ogeler = deger.filter(
          oge => oge !== null && oge !== undefined && oge !== ''
        );
        if (!ogeler.length) return;
        if (ogeler.every(oge => typeof oge !== 'object')) {
          cikti.push({
            anahtar: tamAnahtar,
            etiket,
            deger: ogeler.map(oge => duzDeger(anahtar, oge)).join(', '),
          });
          return;
        }
        if (derinlik < EN_FAZLA_DERINLIK && ogeler.length <= EN_FAZLA_ACILAN_OGE) {
          ogeler.forEach((oge, sira) =>
            gez(oge, `${tamAnahtar}.${sira}`, `${etiket} ${sira + 1}`, derinlik + 1)
          );
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.OGE_SAYISI', { sayi: ogeler.length }),
        });
        return;
      }

      if (typeof deger === 'object') {
        const altSayi = Object.keys(deger).length;
        if (!altSayi) return;
        if (derinlik < EN_FAZLA_DERINLIK) {
          gez(deger, tamAnahtar, etiket, derinlik + 1);
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.ALAN_SAYISI', { sayi: altSayi }),
        });
        return;
      }

      cikti.push({ anahtar: tamAnahtar, etiket, deger: duzDeger(anahtar, deger) });
    });
  };

  gez(nesne, '', '', 0);
  return cikti;
};

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_ads', {
      account_id: props.hesap,
    });
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

const detayAc = async satir => {
  islemdeki.value = satir.id;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_ad_details', {
      ad_id: satir.id,
    });
    const veri = Array.isArray(sonuc?.veri) ? sonuc.veri[0] : sonuc?.veri;
    detay.value = {
      ad: satir.name || satir.id,
      alanlar: alanlaraCevir(veri),
      ozet: sonuc?.ozet || '',
      onizlemeBagi: '',
      onizlemeIstendi: false,
    };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

// Meta onizlemeyi hazir bir iframe etiketi olarak dondurur. Gelen HTML'i
// v-html ile basmak disaridan gelen isaretlemeyi sayfaya enjekte etmek olur;
// bunun yerine yalnizca src adresini cikarip yeni sekmede aciyoruz.
const iframeAdresi = metin => {
  const eslesme = String(metin || '').match(/src="([^"]+)"/);
  if (!eslesme) return '';
  return eslesme[1].replace(/&amp;/g, '&');
};

const onizle = async satir => {
  islemdeki.value = satir.id;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_ad_preview', {
      ad_id: satir.id,
      ad_format: 'DESKTOP_FEED_STANDARD',
    });
    const veri = Array.isArray(sonuc?.veri) ? sonuc.veri[0] : sonuc?.veri;
    const ham = veri?.body || sonuc?.ozet || '';
    detay.value = {
      ad: satir.name || satir.id,
      // body alani ham iframe isaretlemesidir; panelde HTML metni gostermek
      // okunmaz oldugu icin atlaniyor, adresi zaten baglanti olarak veriyoruz.
      alanlar: alanlaraCevir(veri, ['body']),
      ozet: sonuc?.ozet || '',
      onizlemeBagi: iframeAdresi(ham),
      onizlemeIstendi: true,
    };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

const durumDegistir = async satir => {
  const yeniDurum = aktifMi(satir) ? 'PAUSED' : 'ACTIVE';
  const okunurDurum =
    yeniDurum === 'ACTIVE'
      ? t('META_ADS.ORTAK.DURUM_AKTIF')
      : t('META_ADS.ORTAK.DURUM_DURAKLATILDI');
  const soru = t('META_ADS.REKLAMLAR.REKLAM_DURUM_ONAY', {
    ad: satir.name || satir.id,
    durum: okunurDurum,
  });
  if (!window.confirm(soru)) return;

  islemdeki.value = satir.id;
  hata.value = '';
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_update_ad',
      { ad_id: satir.id, status: yeniDurum },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.ORTAK.ISLEM_TAMAM'));
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

const sil = async satir => {
  const ad = satir.name || satir.id;
  // Iki asamali onay: silinen reklam ve gecmis kreatifi geri getirilemez.
  if (!window.confirm(t('META_ADS.REKLAMLAR.REKLAM_SIL_ONAY_1', { ad }))) return;
  if (!window.confirm(t('META_ADS.REKLAMLAR.REKLAM_SIL_ONAY_2', { ad }))) return;

  islemdeki.value = satir.id;
  hata.value = '';
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_delete_ad',
      { ad_id: satir.id },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.ORTAK.ISLEM_TAMAM'));
    if (detay.value?.ad === ad) detay.value = null;
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <div class="flex items-center gap-3 mb-4">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('META_ADS.REKLAMLAR.REKLAM_LISTESI') }}
      </h2>
      <span class="grow" />
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('META_ADS.ORTAK.YENILE')"
        @click="yukle"
      />
    </div>

    <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>
    <p v-if="!hesap" class="text-sm text-n-slate-11">{{ t('META_ADS.ORTAK.HESAP_SECIN') }}</p>

    <div v-else class="flex items-start gap-6">
      <div class="flex-1 min-w-0">
        <AracTablosu
          :satirlar="satirlar"
          :sutunlar="sutunlar"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.REKLAMLAR.REKLAM_YOK')"
        >
          <template #eylemler="{ satir }">
            <div class="flex items-center justify-end gap-1">
              <Button
                variant="faded"
                size="sm"
                :label="t('META_ADS.ORTAK.DETAY')"
                :disabled="islemdeki === satir.id"
                @click="detayAc(satir)"
              />
              <Button
                variant="faded"
                size="sm"
                icon="i-lucide-eye"
                :label="t('META_ADS.REKLAMLAR.ONIZLEME')"
                :disabled="islemdeki === satir.id"
                @click="onizle(satir)"
              />
              <Button
                variant="faded"
                size="sm"
                :icon="aktifMi(satir) ? 'i-lucide-pause' : 'i-lucide-play'"
                :label="
                  aktifMi(satir) ? t('META_ADS.ORTAK.DURDUR') : t('META_ADS.ORTAK.BASLAT')
                "
                :disabled="islemdeki === satir.id"
                @click="durumDegistir(satir)"
              />
              <button
                type="button"
                class="px-2 py-1 text-xs rounded text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
                :disabled="islemdeki === satir.id"
                @click="sil(satir)"
              >
                {{ t('META_ADS.ORTAK.SIL') }}
              </button>
            </div>
          </template>
        </AracTablosu>
      </div>

      <aside
        v-if="detay"
        class="w-80 shrink-0 p-4 border rounded-lg border-n-weak bg-n-surface-1"
      >
        <div class="flex items-start gap-2 mb-3">
          <h3 class="text-sm font-medium text-n-slate-12 break-words">{{ detay.ad }}</h3>
          <span class="grow" />
          <Button variant="faded" size="sm" icon="i-lucide-x" @click="detay = null" />
        </div>

        <a
          v-if="detay.onizlemeBagi"
          :href="detay.onizlemeBagi"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-block mb-3 text-xs underline text-n-brand"
        >
          {{ t('META_ADS.REKLAMLAR.ONIZLEMEYI_AC') }}
        </a>
        <p
          v-else-if="detay.onizlemeIstendi"
          class="mb-3 text-xs text-n-slate-11"
        >
          {{ t('META_ADS.REKLAMLAR.ONIZLEME_YOK') }}
        </p>

        <p v-if="detay.ozet" class="mb-3 text-xs text-n-slate-11">{{ detay.ozet }}</p>
        <dl
          v-if="detay.alanlar.length"
          class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs"
        >
          <template v-for="alan in detay.alanlar" :key="alan.anahtar">
            <dt class="break-words text-n-slate-11">{{ alan.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
          </template>
        </dl>
        <p v-else-if="!detay.onizlemeIstendi" class="text-xs text-n-slate-11">
          {{ t('META_ADS.ORTAK.DETAY_YOK') }}
        </p>
      </aside>
    </div>
  </div>
</template>
